import React from "react";
import { map, filter, distinctUntilChanged, debounceTime } from "rxjs";
import xstream from "state-x-stream";
import PropTypes from "prop-types";
import Band from "./Band";
import BandOutline from "./BandOutline";
import GvarPattern from "../SvgPatterns/GvarPattern";
import StalkPattern from "../SvgPatterns/StalkPattern";
import ZoomService from "../../services/zoomService/zoom.service";
import Chromosome from "../../../../models/Chromosome";

function controller({ props$ }) {
  const { zoomService } = props$.value;
  return {
    isWholeGenome: zoomService.isWholeGenome$,
    xScale: zoomService.xScale$.pipe(
      debounceTime(100),
      map(() => new Date().getTime())
    ),
    selectedChr: zoomService.position$.pipe(
      debounceTime(200),
      filter(Boolean),
      map(({ chrSegments }) => chrSegments),
      filter((x) => x && x.length === 1),
      map((x) => x[0]),
      filter(Boolean),
      map((x) => x.chr),
      distinctUntilChanged()
    ),
  };
}

function Chromosomes({ height, selectedChr, isWholeGenome, zoomService }) {
  const { chrs: allChrs } = zoomService.genome;
  const { scale, r2 } = zoomService.xScale$.value;
  const chrList = isWholeGenome ? allChrs : [selectedChr];
  const bands = [].concat(...allChrs.map((x) => x.bands));
  const chrs = chrList.filter(Boolean);
  const zoomRegion = ({ g0, g1 }) => zoomService.jumpTo(g0 + 1, g1);

  return (
    <>
      <svg width="100%" height="100%" className="tw-absolute tw-inset-0">
        {(chrs.length > 1 ? chrs : []).map((x) => (
          <g key={`${x.genomeId}-${x.name}`}>
            <text
              className="hover:tw-opacity-100 tw-pointer-events-auto tw-text-3xs tw-cursor-pointer tw-fill-current tw-stroke-0 color-1 tw-opacity-20"
              x={scale((x.region.g0 + x.region.g1) / 2)}
              textAnchor="middle"
              y={35}
              data-tooltip={x.name}
              data-tooltip-class="genome-browser-small-tooltip"
              data-tooltip-follow-mouse="1"
              onClick={() => zoomRegion(x.region)}
            >
              {x.shortName}
            </text>
          </g>
        ))}
      </svg>

      <svg width={r2 > 0 ? r2 : "100%"} height={height}>
        <defs>
          <GvarPattern />
          <StalkPattern />
          {chrs.map((x) => (
            <clipPath
              id={`${x.genomeId}-${x.name}`}
              key={`${x.genomeId}-${x.name}-${new Date().getTime()}`}
            >
              <BandOutline
                height={height}
                x1={scale(x.region.g0)}
                x2={scale(x.region.g0 + x.centromere)}
                x3={scale(x.region.g1)}
              />
            </clipPath>
          ))}
        </defs>

        {chrs.map((x) => (
          <g
            key={`${x.genomeId}-${x.name}`}
            data-tooltip-delay="400"
            data-tooltip={x.name}
            data-tooltip-class="genome-browser-small-tooltip"
            data-tooltip-follow-mouse={true}
            onClick={() => zoomRegion(x.region)}
            className={
              bands?.length
                ? "tw-pointer-events-none chr-bands"
                : "tw-pointer-events-auto tw-cursor-pointer chr-bands"
            }
          >
            <BandOutline
              height={height}
              x1={scale(x.region.g0)}
              x2={scale(x.region.g0 + x.centromere)}
              x3={scale(x.region.g1)}
            />

            {isWholeGenome
              ? null
              : x.bands.map((b) => (
                  <Band
                    clipPath={`url(#${x?.genomeId}-${x?.name})`}
                    zoomRegion={(region) => zoomRegion(region)}
                    key={b.region.g0}
                    start={scale(b.region.g0)}
                    end={scale(b.region.g1)}
                    height={height}
                    band={b}
                  />
                ))}
          </g>
        ))}
      </svg>
    </>
  );
}

Chromosomes.propTypes = {
  zoomService: PropTypes.instanceOf(ZoomService).isRequired,
  selectedChr: PropTypes.instanceOf(Chromosome),
  height: PropTypes.number.isRequired,
  isWholeGenome: PropTypes.bool.isRequired,
};

Chromosomes.defaultProps = {
  selectedChr: null,
};

export default xstream(controller)(Chromosomes);
